import { Component, effect, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  BUS_TYPES,
  CAR_TYPES,
  PLANE_TYPES,
  TRAIN_TYPES,
  Transport,
  TransportMode,
  TWO_WHEELS_TYPES,
} from 'src/app/trip-calculator/models/transport';
import { PrestationEmptyStateComponent } from '../prestation-empty-state/prestation-empty-state.component';
import { PrestationModalService } from 'src/app/trip-calculator/services/prestation-modal.service';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { NotificationService } from 'src/app/core/services/notification.service';
import { PopoverModule } from 'primeng/popover';
import { TripService } from 'src/app/trip-calculator/services/trip.service';
import { SectionValidationService } from '../../../services/section-validation.service';
import { ItemValidationBadgeComponent } from '../../item-validation-badge/item-validation-badge.component';
import { MainTransportCheckboxComponent } from '../../custom-input/main-transport-checkbox/main-transport-checkbox.component';
import { LucideAngularModule } from 'lucide-angular';
import { DurationFormatPipe } from '../../../../pipes/duration-format.pipe';
import { TransportFormService } from 'src/app/trip-calculator/services/transport-form.service';

@Component({
  selector: 'app-transport-section',
  imports: [
    TranslocoPipe,
    PrestationEmptyStateComponent,
    ButtonModule,
    ConfirmDialogModule,
    PopoverModule,
    ItemValidationBadgeComponent,
    MainTransportCheckboxComponent,
    LucideAngularModule,
    DurationFormatPipe,
  ],
  templateUrl: './transport-section.component.html',
})
export class TransportSectionComponent {
  tripService = inject(TripService);
  prestationModalService = inject(PrestationModalService);
  translocoService = inject(TranslocoService);
  confirmationService = inject(ConfirmationService);
  notificationService = inject(NotificationService);
  sectionValidationService = inject(SectionValidationService);
  transportFormService = inject(TransportFormService);
  transports?: Transport[];
  hasTransport: boolean = false;
  roadStopLength = 25;

  constructor() {
    effect(() => {
      const trip = this.tripService.trip();
      if (trip) {
        this.transports = trip.transports;
        this.hasTransport = trip.transports.length > 0;
      }
    });
  }
  addTransport = () => {
    this.prestationModalService.addTransport();
  };
  editTransport(index: number) {
    this.prestationModalService.editTransport(index);
  }
  deleteTransport(index: number) {
    this.tripService.deleteTransport(index)?.subscribe();
  }
  confirmDelete(index: number) {
    const message = this.translocoService.translate(
      'confirmation.deleteTransport',
    );
    const header = this.translocoService.translate(
      'confirmation.deleteConfirmation',
    );
    this.confirmationService.confirm({
      message: message,
      header: header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translocoService.translate('yes'),
      rejectLabel: this.translocoService.translate('no'),
      accept: () => this.deleteTransport(index),
    });
  }

  onValidationBadgeClick() {
    // Get the first issue to navigate to the right transport
    const issues =
      this.sectionValidationService.getIssuesForSection('transport');
    const firstErrorIssue = issues.find((issue) => issue.severity === 'error');
    const firstIssue = firstErrorIssue || issues[0];

    if (firstIssue?.index !== undefined) {
      this.prestationModalService.editTransport(firstIssue.index);
    } else {
      this.prestationModalService.addTransport();
    }
  }

  onItemValidationBadgeClick(event: {
    scope: 'transport' | 'accommodation' | 'activity' | 'meal';
    index: number;
  }) {
    if (event.scope === 'transport') {
      this.prestationModalService.editTransport(event.index);
      let step = 3;
      const issues = this.sectionValidationService.getIssuesForItem(
        'transport',
        event.index,
      );
      for (const issue of issues) {
        if (
          [
            'TRANSPORT_TYPE_REQUIRED',
            'TRANSPORT_SUBTYPE_REQUIRED',
            'TRANSPORT_ENERGY_MIX_REQUIRED',
            'TRANSPORT_CRUISE_SUBTYPE_REQUIRED',
            'TRANSPORT_CALCULATION_METHOD_MISSING',
            'TRANSPORT_GENERIC_TYPE_FORBIDDEN',
          ].includes(issue.code)
        ) {
          step = 2;
        }
      }
      this.transportFormService.step.set(step);
    }
  }
  getStep() {}
  getFlightStopAirportCode(transport: Transport, index: number): string {
    if (!transport.flightStops || !transport.flightStops[index]) {
      return '-';
    }
    return transport.flightStops[index].airportCode || '-';
  }

  getRoadStopAddress(transport: Transport, index: number): string {
    const address = transport.roadStops?.[index]?.address ?? '-';
    return address;
  }

  /** Destination is always the last road stop, regardless of how many waypoints exist. */
  getDestinationRoadStopAddress(transport: Transport): string {
    const roadStops = transport.roadStops;
    if (!roadStops || roadStops.length === 0) return '-';
    return roadStops[roadStops.length - 1].address ?? '-';
  }

  getIcon(type: TransportMode) {
    switch (true) {
      case PLANE_TYPES.includes(type):
        return 'plane';
      case TRAIN_TYPES.includes(type):
        return 'train';
      case CAR_TYPES.includes(type):
        return 'car';
      case TWO_WHEELS_TYPES.includes(type):
        return 'bike';
      case BUS_TYPES.includes(type):
        return 'bus';
      case type === TransportMode.CustomFactor:
        return 'calculator';
      default:
        return 'ship';
    }
  }

  shouldDisplayReturnBadge(transport: Transport): boolean {
    const hasDuration = transport.duration && transport.duration > 0;
    const hasDistance = transport.distance && transport.distance > 0;
    return !hasDistance && !hasDuration;
  }
}
